/**
 * NEXUMA — StatusChip
 *
 * One status chip for the whole app. Every status in Nexuma carries three
 * signals at once — an icon, a written label, and a tone — so it stays readable
 * for colour-blind users, in greyscale print-outs and in the PDF/Excel exports
 * that reuse these labels. Colour alone is never the carrier (WCAG 1.4.1).
 *
 * Add a status by extending STATUS_MAP; do not invent one-off pills in feature
 * files, or the vocabulary drifts between the Approval Center, the presence
 * board and the reports.
 *
 *   <StatusChip status="approved" />
 *   <StatusChip status="working" size="sm" />
 *   <StatusChip status="pending" label={t('inbox.status.pending')} />
 */
import React from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';

import CheckCircleRoundedIcon      from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon           from '@mui/icons-material/CancelRounded';
import HourglassTopRoundedIcon     from '@mui/icons-material/HourglassTopRounded';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import PauseCircleFilledRoundedIcon from '@mui/icons-material/PauseCircleFilledRounded';
import DoneAllRoundedIcon          from '@mui/icons-material/DoneAllRounded';
import ScheduleRoundedIcon         from '@mui/icons-material/ScheduleRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import HelpOutlineRoundedIcon      from '@mui/icons-material/HelpOutlineRounded';
import CloudOffRoundedIcon         from '@mui/icons-material/CloudOffRounded';
import SyncRoundedIcon             from '@mui/icons-material/SyncRounded';
import ErrorOutlineRoundedIcon     from '@mui/icons-material/ErrorOutlineRounded';
import BeachAccessRoundedIcon      from '@mui/icons-material/BeachAccessRounded';
import EditNoteRoundedIcon         from '@mui/icons-material/EditNoteRounded';
import LockRoundedIcon             from '@mui/icons-material/LockRounded';
import UndoRoundedIcon             from '@mui/icons-material/UndoRounded';
import Inventory2RoundedIcon       from '@mui/icons-material/Inventory2Rounded';
import AssignmentIndRoundedIcon    from '@mui/icons-material/AssignmentIndRounded';
import BuildRoundedIcon            from '@mui/icons-material/BuildRounded';
import AutoAwesomeRoundedIcon      from '@mui/icons-material/AutoAwesomeRounded';
import ReportProblemRoundedIcon    from '@mui/icons-material/ReportProblemRounded';

/* Tone → palette key. `neutral` deliberately has no semantic colour: it is for
 * states that are neither good nor bad (not started, day off). */
const TONE_PALETTE = {
  success: 'success',
  warning: 'warning',
  error:   'error',
  info:    'info',
  brand:   'primary',
  neutral: null,
};

/**
 * The canonical status vocabulary.
 *   tone       — semantic colour family
 *   Icon       — the shape that carries the meaning without colour
 *   fallback   — Albanian label used when no i18n string is passed in
 */
export const STATUS_MAP = {
  // ── Approvals ──
  pending:      { tone: 'warning', Icon: HourglassTopRoundedIcon,  fallback: 'Në pritje' },
  for_approval: { tone: 'info',    Icon: ScheduleRoundedIcon,      fallback: 'Për aprovim' },
  approved:     { tone: 'success', Icon: CheckCircleRoundedIcon,   fallback: 'Aprovuar' },
  rejected:     { tone: 'error',   Icon: CancelRoundedIcon,        fallback: 'Refuzuar' },
  info_needed:  { tone: 'info',    Icon: HelpOutlineRoundedIcon,   fallback: 'Kërkohet sqarim' },
  // `draft` është neutral me qëllim: një rregullim page i pambaruar nuk pret
  // asgjë nga kush e lexon, ndaj nuk guxon të ngjajë me një kërkesë në pritje.
  draft:        { tone: 'neutral', Icon: EditNoteRoundedIcon,      fallback: 'Draft' },
  // `processed` = aprovuar DHE bartur në një listë page: pika ku shifra u ngriu.
  processed:    { tone: 'info',    Icon: LockRoundedIcon,          fallback: 'Procesuar në pagë' },

  // ── Llogaria e punëtorit ──
  // Punëtori u regjistrua sot, por fillon më vonë: ekziston në HR, kontratë
  // dhe planifikim, kurse hyrja hapet në datën e aktivizimit (workers.activation_date).
  pending_activation: { tone: 'warning', Icon: HourglassTopRoundedIcon, fallback: 'Në pritje të aktivizimit' },

  // ── Regjistri i rregullimeve të pushimeve ──
  // Regjistri është vetëm-shtues: një gabim nuk fshihet, po kthehet me një
  // transaksion kundërt. Prandaj duhen DY fjalë, jo një — rreshti që u zhbë
  // (`reversed`) dhe rreshti që e zhbëri (`reversal`). Etiketat i mbajnë të
  // dallueshme edhe pa ngjyrë; ikona e përbashkët thotë "kthim" për të dyja.
  applied:      { tone: 'success', Icon: CheckCircleRoundedIcon,  fallback: 'Në fuqi' },
  reversed:     { tone: 'neutral', Icon: UndoRoundedIcon,         fallback: 'E kthyer' },
  reversal:     { tone: 'info',    Icon: UndoRoundedIcon,         fallback: 'Transaksion kthyes' },

  // ── Etapa të një periudhe (lehonia) ──
  // `upcoming`/`active`/`completed` përshkruajnë POZITËN e një etape në kohë,
  // jo një vendim. Prandaj nuk ripërdoren `not_started`/`working`/`finished`:
  // ato janë fjalor prezence dhe do të thoshin "punëtori nuk ka filluar sot".
  upcoming:     { tone: 'neutral', Icon: ScheduleRoundedIcon,      fallback: 'Në pritje' },
  active:       { tone: 'success', Icon: PlayCircleFilledRoundedIcon, fallback: 'Në vazhdim' },
  completed:    { tone: 'info',    Icon: DoneAllRoundedIcon,       fallback: 'Përfunduar' },

  // ── Attendance / presence ──
  not_started:  { tone: 'neutral', Icon: ScheduleRoundedIcon,      fallback: 'Pa filluar' },
  working:      { tone: 'success', Icon: PlayCircleFilledRoundedIcon,  fallback: 'Duke punuar' },
  on_break:     { tone: 'warning', Icon: PauseCircleFilledRoundedIcon, fallback: 'Në pauzë' },
  finished:     { tone: 'neutral', Icon: DoneAllRoundedIcon,       fallback: 'Përfunduar' },
  late:         { tone: 'warning', Icon: ScheduleRoundedIcon,      fallback: 'Me vonesë' },
  absent:       { tone: 'error',   Icon: RemoveCircleOutlineRoundedIcon, fallback: 'Mungon' },
  on_leave:     { tone: 'info',    Icon: BeachAccessRoundedIcon,   fallback: 'Në pushim' },
  missing_out:  { tone: 'warning', Icon: ErrorOutlineRoundedIcon,  fallback: 'Pa check-out' },

  // ── Regjistri i aseteve ──
  // Prefiksi `asset_`/`cond_` ekziston sepse fjalët janë të përgjithshme
  // ("available", "new") dhe pa të do të përplaseshin me kuptimin e një moduli
  // tjetër herën tjetër që dikush shton një status.
  //
  // E kuqja i mbetet vetëm asaj që ka shkuar keq: `asset_lost`. Një pajisje e
  // larguar nga qarkullimi nuk është dështim — është fund i jetës së saj, ndaj
  // rri neutrale pranë `retired`-it të pushimeve.
  asset_available: { tone: 'success', Icon: Inventory2RoundedIcon,   fallback: 'Në dispozicion' },
  asset_assigned:  { tone: 'info',    Icon: AssignmentIndRoundedIcon, fallback: 'E caktuar' },
  asset_service:   { tone: 'warning', Icon: BuildRoundedIcon,        fallback: 'Në servis' },
  asset_lost:      { tone: 'error',   Icon: ErrorOutlineRoundedIcon, fallback: 'E humbur' },
  asset_retired:   { tone: 'neutral', Icon: RemoveCircleOutlineRoundedIcon, fallback: 'E larguar' },

  // Gjendja fizike. `cond_good` është neutrale me qëllim: "e mirë" është
  // pritshmëria, jo një lajm i mirë — jeshilja i takon vetëm pajisjes së re.
  cond_new:        { tone: 'success', Icon: AutoAwesomeRoundedIcon,  fallback: 'E re' },
  cond_good:       { tone: 'neutral', Icon: CheckCircleRoundedIcon,  fallback: 'E mirë' },
  cond_damaged:    { tone: 'warning', Icon: ReportProblemRoundedIcon, fallback: 'E dëmtuar' },
  cond_unusable:   { tone: 'error',   Icon: RemoveCircleOutlineRoundedIcon, fallback: 'Jashtë përdorimit' },

  // ── Connectivity ──
  offline:      { tone: 'neutral', Icon: CloudOffRoundedIcon,      fallback: 'Pa internet' },
  syncing:      { tone: 'info',    Icon: SyncRoundedIcon,          fallback: 'Duke sinkronizuar' },
  error:        { tone: 'error',   Icon: ErrorOutlineRoundedIcon,  fallback: 'Gabim' },
};

const SIZES = {
  sm: { px: 0.75, py: 0.125, gap: 0.375, font: '0.66rem', icon: 12, radius: '6px' },
  md: { px: 1,    py: 0.375, gap: 0.5,   font: '0.75rem', icon: 15, radius: '8px' },
};

/**
 * @param {object}  props
 * @param {string}  props.status   Key of STATUS_MAP. Unknown keys render as `error`.
 * @param {string} [props.label]   Translated label; falls back to STATUS_MAP.fallback.
 * @param {'sm'|'md'} [props.size]
 * @param {'soft'|'plain'} [props.variant] `soft` = tinted pill, `plain` = icon + label only.
 */
export default function StatusChip({
  status, label, size = 'md', variant = 'soft', sx, ...rest
}) {
  const theme = useTheme();
  const def = STATUS_MAP[status] || STATUS_MAP.error;
  const s = SIZES[size] || SIZES.md;
  const Icon = def.Icon;

  const paletteKey = TONE_PALETTE[def.tone];
  // Neutral leans on greys so "not started" never reads as a warning.
  // `onSoft`, not `dark` — the label is 0.66–0.75rem and `dark` falls below the
  // 4.5:1 AA floor against its own soft tint.
  const fg = paletteKey ? theme.palette[paletteKey].onSoft : theme.palette.text.secondary;
  const accent = paletteKey ? theme.palette[paletteKey].main : theme.palette.grey[400];
  const bg = variant === 'plain'
    ? 'transparent'
    : (paletteKey ? theme.palette[paletteKey].soft : theme.palette.grey[100]);

  const text = label || def.fallback;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: s.gap,
        px: variant === 'plain' ? 0 : s.px,
        py: variant === 'plain' ? 0 : s.py,
        borderRadius: s.radius,
        bgcolor: bg,
        color: fg,
        border: variant === 'plain' ? 'none' : `1px solid ${alpha(accent, 0.35)}`,
        maxWidth: '100%',
        ...sx,
      }}
      {...rest}
    >
      <Icon aria-hidden sx={{ fontSize: s.icon, color: accent, flexShrink: 0 }} />
      <Typography
        component="span"
        noWrap
        sx={{ fontSize: s.font, fontWeight: 700, lineHeight: 1.4, color: 'inherit' }}
      >
        {text}
      </Typography>
    </Box>
  );
}
